import { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Phone, X, ChevronRight } from 'lucide-react';
import './Navbar.css';

const links = [
  { to: '/', label: 'Home' },
  { to: '/services', label: 'Services' },
  { to: '/portfolio', label: 'Portfolio' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/about', label: 'About' },
  { to: '/careers', label: 'Careers' },
  { to: '/contact', label: 'Contact' },
];

const PHONE_NUMBER = '+918940551811';
const WHATSAPP_NUMBER = '918940551811'; // without '+' for wa.me link
const WHATSAPP_MESSAGE = 'Hi ExploitEye, I would like to know more about your services.';

function HamburgerIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <rect x="2" y="5" width="16" height="2" rx="1" fill="currentColor" />
      <rect x="2" y="9" width="11" height="2" rx="1" fill="currentColor" />
      <rect x="2" y="13" width="16" height="2" rx="1" fill="currentColor" />
    </svg>
  );
}

function WhatsAppIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
    </svg>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Close drawer on Escape key
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const close = () => setOpen(false);
  const toggle = () => setOpen((o) => !o);

  const whatsappHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

  return (
    <>
      <nav className={`navbar ${scrolled ? 'navbar--scrolled' : ''}`}>
        <div className="container navbar__inner">
          <Link to="/" className="navbar__logo" onClick={close}>
            <img src="/logo.png" alt="ExploitEye" className="navbar__logo-img" />
            <span className="navbar__logo-text">ExploitEye</span>
          </Link>

          <ul className="navbar__links">
            {links.map((l) => (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  end={l.to === '/'}
                  className={({ isActive }) =>
                    `navbar__link ${isActive ? 'navbar__link--active' : ''}`
                  }
                >
                  {l.label}
                </NavLink>
              </li>
            ))}
          </ul>

          <div className="navbar__actions">
            <Link to="/contact" className="btn-primary navbar__cta">
              <span className="navbar__cta-full">Get a Quote</span>
              <span className="navbar__cta-short">Quote</span>
            </Link>

            <button
              type="button"
              className="navbar__hamburger"
              onClick={toggle}
              aria-label={open ? 'Close menu' : 'Open menu'}
              aria-expanded={open}
            >
              {open ? <X size={20} /> : <HamburgerIcon />}
            </button>
          </div>
        </div>
      </nav>

      {/* Overlay */}
      <div
        className={`navbar__overlay ${open ? 'navbar__overlay--open' : ''}`}
        onClick={close}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        className={`navbar__drawer ${open ? 'navbar__drawer--open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
      >
        <div className="navbar__drawer-head">
          <div className="navbar__drawer-logo">
            <img src="/logo.png" alt="ExploitEye" />
            <span>ExploitEye</span>
          </div>
          <button
            type="button"
            className="navbar__drawer-close"
            onClick={close}
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>

        <nav className="navbar__drawer-links">
          {links.map((l, i) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === '/'}
              className={({ isActive }) =>
                `navbar__drawer-link ${isActive ? 'navbar__drawer-link--active' : ''}`
              }
              style={{ animationDelay: `${i * 0.05}s` }}
              onClick={close}
            >
              <span className="navbar__drawer-link-label">{l.label}</span>
              <ChevronRight size={16} className="navbar__drawer-link-arrow" />
            </NavLink>
          ))}
        </nav>

        <div className="navbar__drawer-foot">
          {/* Call + WhatsApp side by side (50/50) */}
          <div className="navbar__drawer-contact-row">
            <a
              href={`tel:${PHONE_NUMBER}`}
              className="navbar__drawer-contact navbar__drawer-contact--call"
              onClick={close}
              aria-label="Call us"
            >
              <Phone size={16} />
              <span>Call</span>
            </a>
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              className="navbar__drawer-contact navbar__drawer-contact--whatsapp"
              onClick={close}
              aria-label="Chat on WhatsApp"
            >
              <WhatsAppIcon size={16} />
              <span>WhatsApp</span>
            </a>
          </div>

          {/* Get a Quote button */}
          <Link to="/contact" className="btn-primary navbar__drawer-cta" onClick={close}>
            Get a Free Quote
          </Link>
        </div>
      </div>
    </>
  );
}